import { useState } from "react";
import { ColumnsType } from "antd/es/table";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import Datatable from "../../../../core/common/dataTable";
import FiltersTrackCase, {
  FiltersSelectType,
  initialFilterSelect,
  SearchFormDataType,
} from "../FiltersTrackCase";
import {
  parseDate,
  parseDaysString,
  parseNumber,
} from "../../../../core/methods/sorterMethods";
import EditIcon from "../../../../core/svg-icons/EditIcon";
import TimelineModal from "../TimelineModal";
import ModifyInviteModal from "./ModifyInviteModal";
import {
  dummyData_invited,
  dummyData_totalInvited,
  timelineData_invite,
} from "../../../../core/data/js/dummy-data";
import { statusColors } from "../ApplicantCaseTracking";
import { formattedDate } from "../../../../core/methods/formattedDate";

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  Pending: { bg: "#FFF4E5", color: "#B76E00" },
  "In Progress": { bg: "#E8F0FE", color: "#1A56DB" },
  Accepted: { bg: "#E6F7ED", color: "#0F9D58" },
  Completed: { bg: "#E6F7ED", color: "#0F9D58" },
  Expired: { bg: "#FDECEB", color: "#D93025" },
  Rejected: { bg: "#FDECEB", color: "#D93025" },
};

const DEFAULT_STATUS_STYLE = { bg: "#F1F2F4", color: "#5F6570" };


type Props = {
  status_id?: string;
};

const TotalInviteSent: React.FC<Props> = ({ status_id }) => {
  const [itemList, setItemList] = useState(dummyData_totalInvited);
  const [searchFilter, setSearchFilter] = useState<SearchFormDataType>({
    case_id: "",
    applicant_name: "",
    email: "",
    from_date: "",
    to_date: "",
  });
  const [filterData, setFilterData] = useState<FiltersSelectType>(initialFilterSelect);

  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);


  // ---- columns ----
  const columns: ColumnsType<any> = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      sorter: (a: any, b: any) => a.sno - b.sno,
    },
    {
      title: "Applicant Name",
      dataIndex: "applicantName",
      key: "applicantName",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        a.applicantName.localeCompare(b.applicantName),
    },
    {
      title: "Case ID",
      dataIndex: "caseId",
      key: "caseId",
      onCell: () => cellWidth(190),
      sorter: (a: any, b: any) => parseNumber(a.caseId) - parseNumber(b.caseId),
    },
    {
      title: "Case Spoc",
      dataIndex: "caseSpoc",
      key: "caseSpoc",
      onCell: () => cellWidth(140),
      sorter: (a: any, b: any) => a.caseSpoc.localeCompare(b.caseSpoc),
    },
    {
      title: "Candidate Id",
      dataIndex: "employeeId",
      key: "employeeId",
      onCell: () => cellWidth(160),
      sorter: (a: any, b: any) =>
        parseNumber(a.employeeId) - parseNumber(b.employeeId),
    },
    {
      title: "Email ID",
      dataIndex: "emailId",
      key: "emailId",
      sorter: (a: any, b: any) => a.emailId.localeCompare(b.emailId),
    },
    {
      title: "Days Elapsed",
      dataIndex: "daysElapsed",
      key: "daysElapsed",
      onCell: () => cellWidth(170),
      sorter: (a: any, b: any) =>
        parseDaysString(a.daysElapsed) - parseDaysString(b.daysElapsed),
    },
    {
      title: "Phone No.",
      dataIndex: "phone",
      key: "phone",
      onCell: () => cellWidth(160),
      render: (text: any) => <div>{text || "-"}</div>,
      sorter: (a: any, b: any) => parseNumber(a.phone) - parseNumber(b.phone),
    },
    {
      title: "Invitation Date",
      dataIndex: "invitationDate",
      key: "invitationDate",
      onCell: () => cellWidth(150),
      render: (text: string) => formattedDate(text) ?? "-",
      sorter: (a: any, b: any) =>
        parseDate(a.invitationDate).getTime() -
        parseDate(b.invitationDate).getTime(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      onCell: () => cellWidth(190),
      render: (status: string) => {
        const { bg, color } = STATUS_STYLES[status] || DEFAULT_STATUS_STYLE;
        return (
          <span
            className="badge rounded-pill d-inline-flex align-items-center gap-1 px-3 py-2 fw-medium"
            style={{ background: bg, color }}
          >
            <span
              className="rounded-circle"
              style={{ width: 6, height: 6, background: color }}
            />
            {status}
          </span>
        );
      },
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
    },
    {
      title: "Invitation Expiry Date",
      dataIndex: "inviteExpiryDate",
      key: "inviteExpiryDate",
      onCell: () => cellWidth(180),
      render: (text: string) => formattedDate(text) ?? "-",
      sorter: (a: any, b: any) =>
        parseDate(a.inviteExpiryDate).getTime() -
        parseDate(b.inviteExpiryDate).getTime(),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      onCell: () => cellWidth(160),
      render: () => (
        <div className="dropdown">
          <button
            type="button"
            className="btn btn-sm dropdown-toggle d-inline-flex align-items-center gap-1 border-0"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{
              background: "#E8F0FE",
              color: "#1A56DB",
              fontWeight: 500,
              fontSize: 11,
              borderRadius: 20,
              padding: "6px 14px",
            }}
          >
            <EditIcon />
            Modify Invite
          </button>
          <ul
            className="dropdown-menu shadow-sm border-0 p-1"
            style={{ borderRadius: 10, minWidth: 150 }}
          >
            <li>
              <button
                type="button"
                className="dropdown-item rounded-2 py-2"
                style={{ fontSize: 13 }}
                onClick={() => setShowModifyInvite(true)}
              >
                Fill by HR
              </button>
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(110),
      render: () => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => setShowTimeline(true)}
        >
          <i className="ti ti-eye "></i>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* filters */}
      <FiltersTrackCase
        filterData={filterData}
        searchFilter={searchFilter}
        setFilterData={setFilterData}
        setSearchFilter={setSearchFilter}
      />

      {/* Table  */}
      <div className="card mt-3">
        <Datatable
          dataSource={itemList}
          columns={columns}
          Selection={false}
        // pagination={pagination}
        // handleTableChange={handleTableChange}
        />
      </div>

      {/* Timeline */}
      {showTimeline && (
        <TimelineModal
          timelineData={timelineData_invite}
          onClose={() => setShowTimeline(false)}
        />
      )}

      {/* Modify Invite Modal */}
      {showModifyInvite && (
        <ModifyInviteModal
          handleClose={() => setShowModifyInvite(false)}
          handleSubmit={(d) => {
            console.log(d);
            setShowModifyInvite(false);
          }}
        />
      )}
    </>
  );
};

export default TotalInviteSent;
